import React, { createContext, useContext, useEffect, useMemo } from 'react';
import en from './locales/en.json';
import es from './locales/es.json';

export type Locale = 'en' | 'es';
export type TranslationKey = keyof typeof en;

const translations: Record<Locale, Record<TranslationKey, string>> = {
  en,
  es,
};

type I18nContextValue = {
  language: Locale;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue>({
  language: 'en',
  t: (key) => translations.en[key] || key,
});

const formatTemplate = (template: string, vars?: Record<string, string | number>) => {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => {
    const value = vars[name];
    return value === undefined || value === null ? '' : String(value);
  });
};

export const translate = (language: Locale, key: TranslationKey, vars?: Record<string, string | number>): string => {
  const lang = translations[language] ? language : 'en';
  const template = translations[lang][key] || translations.en[key] || key;
  return formatTemplate(template, vars);
};

export const I18nProvider = ({ language, children }: { language: string; children: React.ReactNode }) => {
  const normalized = (translations[language as Locale] ? language : 'en') as Locale;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = normalized;
    }
  }, [normalized]);

  const value = useMemo<I18nContextValue>(() => ({
    language: normalized,
    t: (key, vars) => translate(normalized, key, vars),
  }), [normalized]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);

export const availableLanguages: Locale[] = ['en', 'es'];
